import { useRef } from "react";
import ReactTooltip from "react-tooltip";
import styled, { css } from "styled-components/macro";
import { Avatar } from "../Avatar/Avatar";
import { TextMdRegular } from "../Typography/TypographyV2";
import { TooltipPortal } from "../TooltipModal/TooltipPortal";

export type AssigneeFieldProps = {
  /**
   * Person’s display name. When empty / null / undefined the field is
   * `Type=Unassigned`; otherwise `Type=Assigned`.
   */
  name?: string | null;
  disabled?: boolean;
  isOpen?: boolean;
  onClick?: () => void;
  disabledTooltip?: string;
  unassignedLabel?: string;
  className?: string;
  "data-testid"?: string;
};

type FieldButtonProps = {
  $disabled: boolean;
  $isOpen: boolean;
};

const interactiveChrome = css`
  box-sizing: border-box;
  background-color: ${({ theme }) =>
    theme.resolve("color_bg_surface_standard")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_md")};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.resolve("color_border_darkest")};
  }

  &:active:not(:disabled) {
    background-color: ${({ theme }) =>
      theme.resolve("color_bg_surface_brand3")};
    border-color: ${({ theme }) => theme.resolve("color_border_brand")};
  }
`;

const openChrome = css`
  background-color: ${({ theme }) => theme.resolve("color_bg_surface_brand3")};
  border-color: ${({ theme }) => theme.resolve("color_border_brand")};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.resolve("color_border_brand")};
  }
`;

const disabledChrome = css`
  background-color: transparent;
  border: ${({ theme }) => theme.resolve("border_md")} solid transparent;
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_none")};
  cursor: not-allowed;
`;

const FieldButton = styled.button.attrs({ type: "button" })<FieldButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
  width: auto;
  max-width: 100%;
  min-height: 32px;
  margin: 0;
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  font: inherit;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  text-align: left;

  ${({ $disabled }) => ($disabled ? disabledChrome : interactiveChrome)}
  ${({ $disabled, $isOpen }) => !$disabled && $isOpen && openChrome}
`;

const Label = styled(TextMdRegular)`
  flex: 1 1 auto;
  min-width: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

/**
 * Compact assignee control: avatar + label.
 * Clicking opens the caller-owned `assigneePopOver` (pass `isOpen` while open).
 *
 * @see Agilis Design System — assigneeField
 */
export function AssigneeField({
  name,
  disabled = false,
  isOpen = false,
  onClick,
  disabledTooltip,
  unassignedLabel = "Unassigned",
  className,
  "data-testid": dataTestId,
}: AssigneeFieldProps) {
  const assignedName = name?.trim() || "";
  const isAssigned = assignedName.length > 0;
  const label = isAssigned ? assignedName : unassignedLabel;
  const tooltipIdRef = useRef(
    `assignee-field-disabled-${Math.random().toString(36).slice(2, 9)}`
  );
  const tooltipId =
    disabled && disabledTooltip ? tooltipIdRef.current : undefined;

  return (
    <>
      <FieldButton
        className={className}
        data-testid={dataTestId}
        disabled={disabled}
        $disabled={disabled}
        $isOpen={isOpen}
        onClick={disabled ? undefined : onClick}
        aria-expanded={isOpen}
        aria-disabled={disabled}
        aria-label={label}
        title={label}
        data-tip={disabled ? disabledTooltip : undefined}
        data-for={disabled ? tooltipId : undefined}
      >
        <Avatar
          type={isAssigned ? "text" : "unassigned"}
          name={isAssigned ? assignedName : undefined}
          size="small"
        />
        <Label color={isAssigned ? "primary" : "tertiary"}>{label}</Label>
      </FieldButton>
      {disabled && disabledTooltip && tooltipId && (
        <TooltipPortal>
          <ReactTooltip id={tooltipId} place="top" effect="solid" />
        </TooltipPortal>
      )}
    </>
  );
}
